/**
 * Orientation Assignment — assign orientations to users directly, in bulk.
 *
 * Renamed from "Cohort Builder" 2026-09-10 (cohort plan decision 6): this
 * page is direct user-to-orientation assignment and stays as is; the real
 * cohort entity gets its own Cohorts tab when the module work lands. The
 * `cohort-builder` view key and the CohortBuilder component name are kept —
 * the key is persisted in localStorage/URLs, and the code rename can ride
 * with the Cohorts tab. The `cohort_progress` view family predates the
 * cohort entity and keeps its name (module-side naming note in the plan).
 *
 * Coordinator workflow:
 *   1. Pick an orientation (from `visible_orientations`)
 *   2. Pick one or more employees (from `visible_employees` — Gap 2 closure)
 *   3. Review the selection, then click Assign — fires `assignOrientation`
 *      once per selected employee
 *
 * The length step (Min5 / Min10 / Min20 / Min60) is gone: single-length mode
 * assigns `Default` for everyone (see `@/lib/orientationVersion`).
 *
 * Notes:
 *   - Roster source switched 2026-06-02 from `cohort_progress`-derived
 *     distincts to the real `visible_employees` view. Newly-added employees
 *     appear immediately; the "must be assigned first" caveat is gone.
 *   - Cross-module session creation in `assign_orientation` is now scoped to
 *     the project (Gap 1 closure on the module side; mp-side provisioning
 *     still pending). Assignment rows are created regardless.
 *
 * Reducer call shape mirrors Ludvig's hookup-plan example exactly:
 *   minetwin.reducers.assignOrientation({
 *     assignmentId, employeeId, orientationId, projectId, orientationVersion
 *   });
 */

import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { useEffect, useMemo, useState } from 'react';
import { NoProjects } from '@/components/NoProjects';
import { NoSiteMembership } from '@/components/NoSiteMembership';
import { useMySiteMembership } from '@/hooks/useMySiteMembership';
import { useSpacetimeDB } from 'spacetimedb/react';
import { type DbConnection as MinetwinConn } from '@sdk/minetwin';
import { assignOrientationArgs, DEFAULT_VERSION } from '@/lib/orientationVersion';
import { useCohortEntities } from '@/hooks/useCohortEntities';
import { useAuthKit } from '@/contexts/AuthProvider';
import {
  layoutProblem,
  layoutSummary,
  type LayoutGroup,
} from '@/lib/groupLayout';
import { CohortTargetCard } from './CohortTargetCard';
import { GroupLayoutCard } from './GroupLayoutCard';
import { TargetToggle, type AssignTarget } from './TargetToggle';
import { useUsers, type UserRow } from '@/hooks/useUsers';
import { useOrientations } from '@/hooks/useOrientations';
import { useCohortProgress } from '@/hooks/useCohortProgress';
import { useOccupations } from '@/hooks/useOccupations';
import { useProjects } from '@/hooks/useProjects';
import { useTweaks } from '@/hooks/useTweaks';
import { useProjectAgents } from '@/hooks/useProjectAgents';
import type { Site, Toast } from '@/data/types';
import { GatedButton } from '../GatedButton';

type CohortBuilderProps = {
  toast: (t: Omit<Toast, 'id'>) => void;
  /** Opens Settings, where the site's onboarding agent is picked. */
  onOpenSettings: () => void;
};

/** Join verb-phrase clauses into a single fluent sentence fragment.
 *
 *   1 → "pick an orientation"
 *   2 → "pick an orientation and select at least one user"
 *   3+ → "pick a site, pick an orientation, and select at least one user"
 *
 *  Oxford-comma form for 3+ items; plain ` and ` for the 2-item case so the
 *  reader doesn't trip over a comma between only two clauses.
 */
function joinClauses(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  const last = items[items.length - 1];
  return `${items.slice(0, -1).join(', ')}, and ${last}`;
}

export function CohortBuilder({ toast, onOpenSettings }: CohortBuilderProps) {
  const minetwinState = useSpacetimeDB('minetwin');
  const { sites, isReady: sitesReady } = useProjects();
  const { tweaks } = useTweaks();
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const projectId = currentSite?.projectName ?? '';
  const membership = useMySiteMembership(projectId);
  // The module refuses every assignment on a site with no onboarding agent
  // ("No agent configured for project …"). Say so before the click, with a
  // way to fix it, instead of harvesting the refusal.
  const { byProjectId: agentFor, isReady: agentsReady } = useProjectAgents();
  const noAgent = agentsReady && projectId !== '' && agentFor(projectId) === null;

  const { users, isReady: usersReady } = useUsers();
  // Scope the picker to the current project (matches the Workforce default).
  const usersInProject = projectId
    ? users.filter((u) => u.projectIds.includes(projectId))
    : users;
  // Orientation source — `visible_orientations` (full catalog) filtered
  // client-side to status === Active. The dedicated server-side
  // `assignable_orientations` view exists in the bindings (Samuel
  // ed2d4b7) but its destructive republish hasn't run yet, so we keep
  // the client-side filter as a safe fallback. Swap to
  // `useAssignableOrientations` after the next republish.
  //
  // Per Cloud Lead 2026-06-18: every coordinator-facing surface
  // (Workforce / Orientation Assignment / Review Queue / My Orientations) is
  // scoped to the topbar's current site. Orientations are filtered to
  // those authored on the current project so a coordinator on Site B
  // doesn't see orientations from Site A in their picker. (Reverses
  // the earlier "orientations are org-wide" framing.)
  const { orientations: allOrientations, isReady: orientationsReady } =
    useOrientations();
  const orientations = useMemo(
    () =>
      allOrientations.filter(
        (o) => o.status.tag === 'Active' && o.projectId === projectId,
      ),
    [allOrientations, projectId],
  );

  const [selectedDisplayName, setSelectedDisplayName] = useState<string>('');
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  // True once the coordinator has hand-edited the selection. Pauses the
  // auto-select effect so a late-arriving user-list update doesn't clobber
  // their manual additions. Resets to false whenever the orientation
  // changes (intent: "give me a fresh suggestion for the new orientation").
  const [manualOverride, setManualOverride] = useState(false);
  const [matchOnly, setMatchOnly] = useState(false);
  /** Coordinator-driven occupation filter for the Pick Users card.
   *  '' = show every occupation. Stacks with the orientation-driven
   *  "Match only" toggle. */
  const [occupationFilter, setOccupationFilter] = useState<string>('');
  const [busy, setBusy] = useState(false);
  // Phase 4: the target picker's second mode. 'users' is today's per-email
  // loop; 'cohort' fires ONE assign_cohort for the whole roster.
  const [target, setTarget] = useState<AssignTarget>('users');
  const [selectedCohortId, setSelectedCohortId] = useState<bigint | null>(null);
  // Assign-time group layout (plan decision 2026-09-18). Empty = everyone
  // individually, which is why picking a cohort resets it: the layout is a
  // statement about ONE roster.
  const [groups, setGroups] = useState<LayoutGroup[]>([]);
  const { user } = useAuthKit();
  const currentUserEmail = user?.email ?? '';
  const { cohorts: allCohorts, isReady: cohortsReady } = useCohortEntities(projectId);
  const activeCohorts = useMemo(
    () => allCohorts.filter((c) => !c.archived),
    [allCohorts],
  );
  const selectedCohort = useMemo(
    () => activeCohorts.find((c) => c.cohortId === selectedCohortId) ?? null,
    [activeCohorts, selectedCohortId],
  );

  const connOnline = minetwinState.isActive;
  // Resolve the picked Databricks display_name to the module's actual
  // orientation row (by display_name match). If no match exists the
  // coordinator can't assign — the module catalog needs the orientation
  // imported first.
  const selectedOrientation = useMemo(
    () =>
      selectedDisplayName
        ? orientations.find((o) => o.displayName === selectedDisplayName) ?? null
        : null,
    [orientations, selectedDisplayName],
  );
  // The set of occupations this orientation targets (empty = universal). Memoized
  // so its identity is stable across renders (it feeds effect/memo deps below).
  const occupationTarget = useMemo<string[]>(
    () => selectedOrientation?.occupationTarget ?? [],
    [selectedOrientation],
  );

  // Occupation filter dropdown in the Pick Users card — sourced from
  // the same Databricks `employee_roster.job_title` catalog that the
  // Workforce → Add User dropdown uses (Cloud Lead 2026-06-22). The
  // two surfaces stay in lock-step: any job title a coordinator could
  // assign during onboarding is also pickable here, even if no user
  // on this site currently carries that occupation yet. Cached at the
  // hook level so this re-mount doesn't pay the cold-start tax.
  const { occupations: occupationCatalog } = useOccupations();
  const availableOccupations = useMemo(
    () => occupationCatalog.map((o) => o.title),
    [occupationCatalog],
  );

  // Apply the orientation-driven "Match only" toggle AND the
  // coordinator-driven occupation filter, in that order. Both compose;
  // either can be active alone.
  const usersForPicker = useMemo(() => {
    let rows = usersInProject;
    if (matchOnly && occupationTarget.length > 0) {
      rows = rows.filter((u) => occupationTarget.includes(u.occupation));
    }
    if (occupationFilter) {
      rows = rows.filter((u) => u.occupation === occupationFilter);
    }
    return rows;
  }, [usersInProject, matchOnly, occupationTarget, occupationFilter]);

  // Lower-cased emails already assigned to the CURRENTLY SELECTED
  // orientation on the current site. The lock is per-orientation only
  // (revised 2026-07-08; supersedes the 2026-07-01 one-orientation-per-
  // site rule, which permanently locked anyone with any non-Cancelled
  // assignment anywhere on the site): a user on Orientation A can still
  // be enrolled on B, they just can't be assigned A twice.
  //
  // `Cancelled` rows count as "unassigned" (that's exactly what
  // `cancel_assignment` does on the Orientations Unassign button), so
  // they're excluded here — matches the coordinator's mental model
  // that unassigning frees the slot. `Completed` rows still block:
  // the server keeps one assignment row per (project, email,
  // orientation) and retakes go through send_back, not a fresh assign.
  const { rows: assignmentRows } = useCohortProgress();
  const assignedByEmail = useMemo(() => {
    const s = new Set<string>();
    if (!projectId || !selectedOrientation) return s;
    for (const a of assignmentRows) {
      if (a.projectId !== projectId) continue;
      if (a.orientationId !== selectedOrientation.orientationId) continue;
      if (a.status.tag === 'Cancelled') continue;
      s.add(a.email.toLowerCase());
    }
    return s;
  }, [assignmentRows, projectId, selectedOrientation]);

  // Auto-select users whose occupation matches the chosen orientation.
  // Triggered ONLY on selection change so loading more users in another
  // tab doesn't clobber manual edits.
  useEffect(() => {
    if (selectedDisplayName === '') return;
    if (manualOverride) return;
    if (occupationTarget.length === 0) {
      // Orientation has no occupation_target — nothing to match against.
      // Leave whatever the coordinator already has selected instead of
      // wiping. Previously this called setSelectedEmails(new Set()),
      // which clobbered manual picks the moment a no-target orientation
      // was selected (i.e. the Add-User-flow skeletons + the 2 imported
      // orientations whose targets are blank).
      return;
    }
    const matches = usersInProject
      .filter((u) => occupationTarget.includes(u.occupation))
      // Never auto-select someone who's already assigned to this
      // orientation. Otherwise the "match" wouldn't reflect the actual
      // assign-eligibility state.
      .filter((u) => !assignedByEmail.has(u.email.toLowerCase()))
      .map((u) => u.email);
    setSelectedEmails(new Set(matches));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDisplayName, occupationTarget, manualOverride, assignedByEmail]);

  function handleOrientationChange(displayName: string) {
    setSelectedDisplayName(displayName);
    setManualOverride(false); // re-enable auto-select for the new pick
    // The group layout is a statement about one (cohort, orientation):
    // who may be grouped depends on who already started THIS orientation.
    setGroups([]);
  }

  function handleCohortSelect(cohortId: bigint) {
    setSelectedCohortId(cohortId);
    setGroups([]);
  }

  // Roster members who may be placed in a group: a live InProgress or
  // Completed row for the selected orientation refuses the WHOLE layout
  // server-side (same rule as create_group_run), so the picker doesn't
  // offer them. A NotStarted row is fine — the module adopts it.
  const startedByEmail = useMemo(() => {
    const s = new Set<string>();
    if (!projectId || !selectedOrientation) return s;
    for (const a of assignmentRows) {
      if (a.projectId !== projectId) continue;
      if (a.orientationId !== selectedOrientation.orientationId) continue;
      if (a.status.tag === 'InProgress' || a.status.tag === 'Completed')
        s.add(a.email.toLowerCase());
    }
    return s;
  }, [assignmentRows, projectId, selectedOrientation]);
  // Samuel 2026-09-21: a non-groupable member renders as a DISABLED chip
  // with the reason on hover, never silently omitted — a vanished chip
  // reads as a bug ("where did my third member go?").
  const groupBlockReasons = useMemo(() => {
    const m = new Map<string, string>();
    if (!selectedOrientation) return m;
    for (const e of selectedCohort?.members ?? []) {
      if (startedByEmail.has(e.toLowerCase()))
        m.set(
          e,
          `${e} has already started or completed orientation "${selectedOrientation.displayName}" — to free them, unassign it on the Orientations page (or, once completed, send them back from the Review Queue).`,
        );
    }
    return m;
  }, [selectedCohort, selectedOrientation, startedByEmail]);

  // User-facing copy: avoid internal UI references like "(left card)" — they
  // are meaningless on mobile, and rot whenever the layout changes. Each
  // clause is a complete verb-phrase so it reads naturally when joined into
  // a single sentence below.
  const missing: string[] = [];
  if (!connOnline) missing.push('wait for the MineTwin connection to come back online');
  if (!projectId) missing.push('pick a site from the topbar');
  if (noAgent) missing.push('set an onboarding agent for this site in Settings');
  if (!selectedDisplayName) missing.push('pick an orientation');
  if (target === 'users' && selectedEmails.size === 0)
    missing.push('select at least one user');
  if (target === 'cohort' && !selectedCohort) missing.push('pick a cohort');
  // Pre-check the module's refused-whole layout rules so a shape the
  // portal can see wrong never harvests the server refusal.
  const groupIssue =
    target === 'cohort' && selectedCohort && groups.length > 0
      ? layoutProblem(groups, currentUserEmail)
      : null;
  if (groupIssue) missing.push(`fix the group layout (${groupIssue.replace(/\.$/, '')})`);
  const canAssign = missing.length === 0;
  const disabledReason =
    missing.length > 0 ? `To assign, ${joinClauses(missing)}.` : undefined;

  function toggleEmail(email: string) {
    // Silently ignore already-assigned emails. The picker card renders
    // them disabled so this normally isn't reachable, but a keyboard user
    // could still trigger the change; skip so the "N of M" counter and
    // the assigned set never drift out of sync with the disabled UI.
    if (assignedByEmail.has(email.toLowerCase())) return;
    setManualOverride(true);
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  function selectAllUsers() {
    setManualOverride(true);
    // "All" means "all pickable users" — skip anyone already assigned to
    // the selected orientation so the button doesn't lie about what it
    // does.
    setSelectedEmails(
      new Set(
        usersForPicker
          .filter((u) => !assignedByEmail.has(u.email.toLowerCase()))
          .map((u) => u.email),
      ),
    );
  }

  function clearUsers() {
    setManualOverride(true);
    setSelectedEmails(new Set());
  }

  /** Coordinator-facing reset: throw away manual edits and re-apply the
   *  auto-select for the current orientation. */
  function resetToOccupationMatch() {
    setManualOverride(false);
  }

  /** Cohort target: ONE awaited reducer call assigns the whole roster
   *  WITH its group layout (decision 2026-09-18) — one shared session per
   *  group from the start, individuals for everyone else, no assignment
   *  for a roster leader. The module applies the collision rule per
   *  individual member and never fails the batch on a skip; an
   *  inconsistent LAYOUT is refused whole (pre-checked above). */
  async function handleAssignCohort() {
    const conn = minetwinState.getConnection() as MinetwinConn | null;
    if (!conn || !selectedOrientation || !selectedCohort) return;
    setBusy(true);
    try {
      await conn.reducers.assignCohortWithGroups({
        cohortId: selectedCohort.cohortId,
        orientationId: selectedOrientation.orientationId,
        orientationVersion: DEFAULT_VERSION,
        groups: groups.map((g) => ({
          name: g.name.trim(),
          memberEmails: g.memberEmails,
          leaderEmail: g.leaderEmail,
          mode: { tag: g.modeTag } as any,
          allowLateJoin: g.allowLateJoin,
          autoStallSeconds: g.autoStallSeconds,
        })),
      });
      toast({
        title: `${selectedDisplayName} assigned to "${selectedCohort.name}"`,
        sub: `${layoutSummary(selectedCohort.members, groups, currentUserEmail)} — progress, groups and any skipped members show on the Cohorts tab`,
        kind: 'good',
      });
      setSelectedCohortId(null);
      setGroups([]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Cohort assign failed', sub: msg.slice(0, 200), kind: 'bad' });
    } finally {
      setBusy(false);
    }
  }

  function handleAssign() {
    if (!canAssign) {
      toast({
        title: 'Cannot assign',
        sub: disabledReason ?? '',
        kind: 'warn',
      });
      return;
    }
    if (target === 'cohort') {
      void handleAssignCohort();
      return;
    }
    const conn = minetwinState.getConnection() as MinetwinConn | null;
    if (!conn) {
      toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
      return;
    }

    if (!selectedOrientation) {
      // Picker is sourced from `visible_orientations`, so this is
      // unreachable in practice. Guard so a stale `selectedDisplayName`
      // can't fire `assign_orientation` with an empty id.
      toast({ title: 'Orientation not resolved', kind: 'bad' });
      return;
    }
    const orientationId = selectedOrientation.orientationId;
    setBusy(true);
    const emails = Array.from(selectedEmails);
    let succeeded = 0;
    const failures: string[] = [];
    try {
      for (const email of emails) {
        try {
          // Phase 3: assign_orientation now keys on email (the stable
          // cross-state FK). Pre-login users' assignments wait with
          // user_id = None and bind on their first connect.
          // assignment_id is server-assigned (auto_inc); the module enforces one
          // assignment per (project, email, orientation), so a re-assign updates
          // the not-started row's version rather than duplicating it.
          // Single-length mode: always `Default` (see @/lib/orientationVersion).
          conn.reducers.assignOrientation(
            assignOrientationArgs(email, orientationId, projectId),
          );
          succeeded += 1;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          failures.push(`${email}: ${msg}`);
        }
      }
      const assigneeLabel = formatAssigneeList(emails, users);
      const siteLabel = currentSite?.name ?? projectId;
      if (failures.length === 0) {
        toast({
          title: `${selectedDisplayName} assigned`,
          sub: `to ${assigneeLabel} on ${siteLabel}`,
          kind: 'good',
        });
      } else {
        const sampleFailure = failures[0]?.slice(0, 160) ?? '';
        toast({
          title: `${selectedDisplayName}: ${succeeded} of ${emails.length} assigned`,
          sub: sampleFailure
            ? `Failed: ${sampleFailure}${failures.length > 1 ? ` (+${failures.length - 1} more)` : ''}`
            : `${failures.length} failed`,
          kind: 'warn',
        });
      }
      // Always clear the selection after an Assign attempt — success OR
      // partial failure. Cloud Lead 2026-07-01: "once you assign, i want
      // you to un select everything." Two motivations:
      //   1. The freshly-assigned users show up as ALREADY ASSIGNED on
      //      the next re-render (see cohort_progress subscription), so
      //      leaving them checked is a lie about the pickable state.
      //   2. If some failed, retrying is a deliberate action — pick them
      //      again from the picker instead of an implicit "keep going".
      clearUsers();
    } finally {
      setBusy(false);
    }
  }

  const totalAssignments = selectedEmails.size;

  if (sitesReady && sites.length === 0) return <NoProjects pageName="Orientation Assignment" />;
  if (membership.isReady && currentSite && !membership.isMember)
    return <NoSiteMembership pageName="Orientation Assignment" siteName={currentSite.name} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 18 }}>Orientation Assignment</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {orientations.length} orientations · {usersInProject.length} users
            {currentSite ? <> · {currentSite.name}</> : null}
            {connOnline ? null : <span style={{ color: 'var(--bad)' }}> · offline</span>}
          </div>
        </div>
      </div>
      {noAgent ? (
        <div
          role="alert"
          style={{
            margin: '0 18px 10px',
            padding: '10px 14px',
            border: '1px solid var(--warn)',
            borderRadius: 'var(--r, 8px)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 12.5,
          }}
        >
          <span style={{ flex: 1 }}>
            <b>{currentSite?.name ?? 'This site'} has no onboarding agent.</b> Assignments
            are refused until one is picked in Settings.
          </span>
          <button type="button" className="btn sm primary" onClick={onOpenSettings}>
            Open Settings
          </button>
        </div>
      ) : null}

      <div className="page-body">
        <div
          className="grid"
          style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}
        >
          <OrientationPickerCard
            // Sourced only from `visible_orientations` (STDB), Active rows
            // only. Authoring happens in Unreal; the portal does not
            // create orientations.
            orientations={orientations}
            isReady={orientationsReady}
            selectedDisplayName={selectedDisplayName}
            onSelect={handleOrientationChange}
          />
          <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
            <TargetToggle value={target} onChange={setTarget} />
            {target === 'users' ? (
              <UserPickerCard
                users={usersForPicker}
                isReady={usersReady}
                selectedEmails={selectedEmails}
                assignedByEmail={assignedByEmail}
                occupationTarget={occupationTarget}
                matchOnly={matchOnly}
                onMatchOnlyToggle={() => setMatchOnly((v) => !v)}
                manualOverride={manualOverride}
                onResetMatch={resetToOccupationMatch}
                availableOccupations={availableOccupations}
                occupationFilter={occupationFilter}
                onOccupationFilterChange={setOccupationFilter}
                onToggle={toggleEmail}
                onSelectAll={selectAllUsers}
                onClear={clearUsers}
              />
            ) : (
              <>
                <CohortTargetCard
                  cohorts={activeCohorts}
                  isReady={cohortsReady}
                  selectedOrientationId={selectedOrientation?.orientationId ?? null}
                  selectedCohortId={selectedCohortId}
                  onSelect={handleCohortSelect}
                />
                {selectedCohort ? (
                  <GroupLayoutCard
                    rosterEmails={selectedCohort.members}
                    blockedReasons={groupBlockReasons}
                    siteEmails={usersInProject.map((u) => u.email)}
                    currentUserEmail={currentUserEmail}
                    groups={groups}
                    onChange={setGroups}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>

        <AssignReviewCard
          orientationName={selectedOrientation?.displayName ?? null}
          orientationAutoAssign={selectedOrientation?.autoAssign ?? false}
          siteName={currentSite?.name ?? null}
          selectedEmails={selectedEmails}
          users={usersInProject}
          canAssign={canAssign}
          busy={busy}
          disabledReason={disabledReason}
          onAssign={handleAssign}
          cohortTarget={
            target === 'cohort'
              ? selectedCohort
                ? {
                    name: selectedCohort.name,
                    memberCount: selectedCohort.members.length,
                    layout: layoutSummary(
                      selectedCohort.members,
                      groups,
                      currentUserEmail,
                    ),
                    layoutIssue: groupIssue,
                  }
                : null
              : undefined
          }
        />
      </div>
    </>
  );
}

// -- Sub-components --------------------------------------------------------

/**
 * Review & Assign card. Replaces the prior one-line-buried-in-sub summary
 * (Cloud Lead 2026-06-24 — "user does not understand what it selected, it
 * comes in the small text"). Lays the choices that drive the
 * `assignOrientation` call out as labelled rows at body-text size so a
 * coordinator can confirm what they're about to do before clicking.
 */
function AssignReviewCard({
  orientationName,
  orientationAutoAssign,
  siteName,
  selectedEmails,
  users,
  canAssign,
  busy,
  disabledReason,
  onAssign,
  cohortTarget,
}: {
  orientationName: string | null;
  orientationAutoAssign: boolean;
  siteName: string | null;
  selectedEmails: Set<string>;
  users: UserRow[];
  canAssign: boolean;
  busy: boolean;
  disabledReason: string | undefined;
  onAssign: () => void;
  /** Set when the target picker is in Cohort mode: replaces the Users
   *  row and the button label with the roster-level act. `layout` is the
   *  group-layout summary line ("everyone individually (N)" / "2 groups
   *  (5 members) · …"); `layoutIssue` is the pre-check's refused-whole
   *  finding, rendered VISIBLY (#170 — the tooltip alone was missable). */
  cohortTarget?: {
    name: string;
    memberCount: number;
    layout: string;
    layoutIssue: string | null;
  } | null;
}) {
  const totalAssignments = selectedEmails.size;
  const userList = useMemo(
    () => formatAssigneeList(Array.from(selectedEmails), users, 4),
    [selectedEmails, users],
  );

  return (
    <div className="card mt-14">
      <div className="card-head">
        <div>
          <h3>Review &amp; assign</h3>
          <div className="sub">
            Confirm what will be created, then click Assign.
          </div>
        </div>
      </div>
      <div className="card-body review-card-body">
        <ReviewRow label="Orientation">
          {orientationName ? (
            <>
              <span className="review-value">{orientationName}</span>
              {orientationAutoAssign ? (
                <span className="review-autoassign-tag">auto-assign</span>
              ) : null}
            </>
          ) : (
            <ReviewPlaceholder>
              pick one in the <b>Pick orientation</b> card
            </ReviewPlaceholder>
          )}
        </ReviewRow>

        <ReviewRow label="Site">
          {siteName ? (
            <span className="review-value">{siteName}</span>
          ) : (
            <ReviewPlaceholder>pick one in the topbar</ReviewPlaceholder>
          )}
        </ReviewRow>

        {cohortTarget !== undefined && cohortTarget !== null ? (
          <>
            <ReviewRow label={`Cohort (${cohortTarget.memberCount})`}>
              <span className="review-value">{cohortTarget.name}</span>
            </ReviewRow>
            <ReviewRow label="Layout">
              <span className="review-value">{cohortTarget.layout}</span>
              {cohortTarget.layoutIssue ? (
                <span style={{ color: 'var(--warn)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon of={Icons.status.warning} size={12} /> {cohortTarget.layoutIssue}
                </span>
              ) : null}
            </ReviewRow>
          </>
        ) : cohortTarget === null ? (
          <ReviewRow label="Cohort">
            <ReviewPlaceholder>
              pick one in the <b>Pick cohort</b> card
            </ReviewPlaceholder>
          </ReviewRow>
        ) : (
          <ReviewRow
            label={`Users${totalAssignments > 0 ? ` (${totalAssignments})` : ''}`}
          >
            {totalAssignments > 0 ? (
              <span className="review-users">{userList}</span>
            ) : (
              <ReviewPlaceholder>
                check one or more in the <b>Pick users</b> card
              </ReviewPlaceholder>
            )}
          </ReviewRow>
        )}

        <div className="review-actions">
          {/* The prior `⚠ To assign, …` line was removed 2026-07-01 —
              it read like an error immediately after a successful
              assign (`handleAssign` clears `selectedEmails` to zero on
              the next paint, which flips `disabledReason` truthy again).
              The review rows above already explain each missing choice
              inline ("— pick one in the Pick orientation card", etc.),
              so this line was redundant even before the post-assign
              flash — it was the flash that made it feel wrong.
              `disabledReason` still lives on the button's hover title
              (aria-disabled, so the title actually shows, #262). */}
          <GatedButton
            className="btn primary review-actions-btn"
            busy={busy}
            reason={canAssign ? null : (disabledReason ?? null)}
            onClick={onAssign}
          >
            {busy
              ? 'Assigning…'
              : cohortTarget
                ? `Assign to cohort (${cohortTarget.memberCount} member${cohortTarget.memberCount === 1 ? '' : 's'})`
                : totalAssignments
                  ? `Assign ${totalAssignments} user${totalAssignments === 1 ? '' : 's'}`
                  : 'Assign'}
          </GatedButton>
        </div>
      </div>
    </div>
  );
}

/** Labeled row inside the AssignReviewCard. Label on the left at the
 *  small "Field:" altitude, body content on the right at body size. */
function ReviewRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="review-row">
      <span className="review-row-label">{label}</span>
      <span className="review-row-value">{children}</span>
    </div>
  );
}

function ReviewPlaceholder({ children }: { children: React.ReactNode }) {
  return <span className="review-placeholder">— {children}</span>;
}

function OrientationPickerCard({
  orientations,
  isReady,
  selectedDisplayName,
  onSelect,
}: {
  orientations: {
    orientationId: string;
    displayName: string;
    occupationTarget: string[];
    description: string;
    autoAssign: boolean;
  }[];
  isReady: boolean;
  selectedDisplayName: string;
  onSelect: (displayName: string) => void;
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orientations;
    return orientations.filter(
      (o) =>
        o.displayName.toLowerCase().includes(q) ||
        (o.description ?? '').toLowerCase().includes(q),
    );
  }, [orientations, query]);
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>
            Pick orientation{' '}
            <span className="text-muted" style={{ fontWeight: 500 }}>
              · {filtered.length} of {orientations.length}
            </span>
          </h3>
          <div className="sub">
            Orientations from the MineTwin module
            (<code>visible_orientations</code>), Active only. Authoring
            happens in Unreal; the portal does not create orientations.
          </div>
        </div>
        <input
          type="search"
          className="inp"
          placeholder="Search orientations…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ fontSize: 12, padding: '4px 8px', maxWidth: 200 }}
          aria-label="Search orientations by name or description"
        />
      </div>
      {!isReady ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: 12.5,
          }}
        >
          Loading orientations…
        </div>
      ) : orientations.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: 12.5,
          }}
        >
          No Active orientations yet — Samuel's Unreal authoring (or
          an <code>import_orientations</code> JSON) seeds this list.
        </div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: 12.5,
          }}
        >
          No orientations match “{query}”.
        </div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {filtered.map((o) => {
            const selected = o.displayName === selectedDisplayName;
            return (
              <button
                type="button"
                key={o.orientationId}
                className="nav-item"
                onClick={() => onSelect(o.displayName)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 14px',
                  borderRadius: 0,
                  borderTop: '1px solid var(--border-soft, var(--border))',
                  background: selected
                    ? 'var(--amber-soft, var(--panel-2))'
                    : 'transparent',
                  borderLeft: selected
                    ? '2px solid var(--amber, var(--good))'
                    : '2px solid transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    border: '2px solid var(--border-2, var(--border))',
                    background: selected
                      ? 'var(--amber, var(--good))'
                      : 'transparent',
                    marginTop: 3,
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.displayName}
                    </span>
                    {o.autoAssign ? (
                      <span
                        title="auto_assign is on — workers whose occupation matches occupation_target are enrolled automatically when added."
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '1px 6px',
                          borderRadius: 8,
                          background: 'var(--good-soft, rgba(92,200,154,0.15))',
                          color: 'var(--good, #5CC89A)',
                          flexShrink: 0,
                        }}
                      >
                        auto
                      </span>
                    ) : null}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 11,
                      color: 'var(--muted)',
                      marginTop: 2,
                    }}
                  >
                    {o.occupationTarget.length > 0 ? (
                      <>
                        {o.occupationTarget.join(', ')} ·{' '}
                        <code style={{ fontSize: 10 }}>{o.orientationId}</code>
                      </>
                    ) : (
                      <>
                        <span
                          style={{ color: 'var(--warn)' }}
                          title="No occupation_target on this orientation — auto-select can't fire."
                        >
                          no target
                        </span>{' '}
                        ·{' '}
                        <code style={{ fontSize: 10 }}>{o.orientationId}</code>
                      </>
                    )}
                  </span>
                  {o.description ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: 'var(--muted-2, var(--muted))',
                        marginTop: 3,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {o.description}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UserPickerCard({
  users,
  isReady,
  selectedEmails,
  assignedByEmail,
  occupationTarget,
  matchOnly,
  onMatchOnlyToggle,
  manualOverride,
  onResetMatch,
  availableOccupations,
  occupationFilter,
  onOccupationFilterChange,
  onToggle,
  onSelectAll,
  onClear,
}: {
  users: UserRow[];
  isReady: boolean;
  selectedEmails: Set<string>;
  /** Lower-cased emails already assigned to the selected orientation.
   *  Rendered as disabled rows with an "Already assigned" pill. */
  assignedByEmail: Set<string>;
  occupationTarget: string[];
  matchOnly: boolean;
  onMatchOnlyToggle: () => void;
  manualOverride: boolean;
  onResetMatch: () => void;
  /** Distinct occupations present in `users`, sorted; used to populate
   *  the coordinator-driven filter dropdown. */
  availableOccupations: string[];
  /** Currently filtered occupation; `''` means show every occupation. */
  occupationFilter: string;
  onOccupationFilterChange: (value: string) => void;
  onToggle: (email: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.displayName.toLowerCase().includes(q) ||
        u.occupation.toLowerCase().includes(q),
    );
  }, [users, query]);
  // Users who ARE pickable (not already assigned). The header counter and
  // the All button both track "of pickable" so a coordinator understands
  // that the number they see isn't the raw roster count when an
  // orientation gates some rows out.
  const pickableCount = useMemo(
    () =>
      users.reduce(
        (acc, u) => acc + (assignedByEmail.has(u.email.toLowerCase()) ? 0 : 1),
        0,
      ),
    [users, assignedByEmail],
  );
  const allOn = selectedEmails.size > 0 && selectedEmails.size >= pickableCount;
  const hasTarget = occupationTarget.length > 0;
  const targetLabel = occupationTarget.join(', ');
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>
            Pick users{' '}
            <span className="text-muted" style={{ fontWeight: 500 }}>
              · {selectedEmails.size} of {pickableCount}
              {pickableCount < users.length ? (
                <>
                  {' '}
                  <span
                    title={`${users.length - pickableCount} user${
                      users.length - pickableCount === 1 ? '' : 's'
                    } already assigned to this orientation — unassign on the Orientations page to make them available again.`}
                    style={{ color: 'var(--muted)' }}
                  >
                    ({users.length - pickableCount} already assigned)
                  </span>
                </>
              ) : null}
            </span>
          </h3>
          <div className="sub">
            {hasTarget ? (
              <>Targets <b>{targetLabel}</b> — matching users pre-selected.</>
            ) : (
              <>This orientation has no target — pick users manually.</>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 11,
              color: 'var(--muted)',
            }}
            title="Filter the user list by a specific occupation. Stacks with Match only."
          >
            Occupation
            <select
              className="inp"
              style={{ fontSize: 12, padding: '2px 6px', maxWidth: 180 }}
              value={occupationFilter}
              onChange={(e) => onOccupationFilterChange(e.target.value)}
              disabled={availableOccupations.length === 0}
            >
              {/* A disabled select hides its title, so the reason is the
                  visible option itself (#262). */}
              <option value="">
                {availableOccupations.length === 0 ? '(none on this site)' : '(all)'}
              </option>
              {availableOccupations.map((occ) => (
                <option key={occ} value={occ}>
                  {occ}
                </option>
              ))}
            </select>
          </label>
          {hasTarget ? (
            <button
              type="button"
              className={'btn sm ' + (matchOnly ? '' : 'ghost')}
              onClick={onMatchOnlyToggle}
              title={
                matchOnly
                  ? 'Show every user on this site'
                  : `Only show users assigned to "${targetLabel}"`
              }
            >
              Match only
            </button>
          ) : null}
          {hasTarget && manualOverride ? (
            <button
              type="button"
              className="btn sm ghost"
              onClick={onResetMatch}
              title="Discard manual edits and re-apply the orientation match"
            >
              Reset match
            </button>
          ) : null}
          <GatedButton
            className="btn sm ghost"
            onClick={onSelectAll}
            reason={
              users.length === 0
                ? 'No users to select'
                : allOn
                  ? 'Everyone shown is already selected'
                  : null
            }
          >
            All
          </GatedButton>
          <GatedButton
            className="btn sm ghost"
            onClick={onClear}
            reason={selectedEmails.size === 0 ? 'Nothing is selected' : null}
          >
            None
          </GatedButton>
        </div>
      </div>
      {!isReady ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: 12.5,
          }}
        >
          Loading users…
        </div>
      ) : users.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: 12.5,
          }}
        >
          No users on this site yet. Add them in the Workforce view first.
        </div>
      ) : (
        <>
          <div
            style={{
              padding: '8px 14px',
              borderTop: '1px solid var(--border-soft, var(--border))',
            }}
          >
            <input
              type="search"
              className="inp"
              placeholder="Search by email, name, or occupation…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ fontSize: 12, padding: '4px 8px', width: '100%' }}
              aria-label="Search users by email, name, or occupation"
            />
          </div>
          {filteredUsers.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: 12.5,
              }}
            >
              No users match “{query}”.
            </div>
          ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {filteredUsers.map((u) => {
            const checked = selectedEmails.has(u.email);
            const matches = hasTarget && occupationTarget.includes(u.occupation);
            // "Already assigned" wins over every other row state — a
            // matched user who's already assigned to this orientation
            // can't be re-picked, so we suppress the ✓ match badge and
            // grey the row instead.
            const alreadyAssigned = assignedByEmail.has(u.email.toLowerCase());
            return (
              <label
                key={u.email}
                title={
                  alreadyAssigned
                    ? 'Already assigned to this orientation. To free them, unassign it on the Orientations page (Orientations → By user). To have them redo a completed run instead, send them back from the Review Queue.'
                    : undefined
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 14px',
                  borderTop: '1px solid var(--border-soft, var(--border))',
                  cursor: alreadyAssigned ? 'not-allowed' : 'pointer',
                  opacity: alreadyAssigned ? 0.55 : 1,
                  background: checked
                    ? 'var(--amber-soft, var(--panel-2))'
                    : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={alreadyAssigned}
                  onChange={() => onToggle(u.email)}
                  style={{ width: 14, height: 14 }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {u.email}
                    {alreadyAssigned ? (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--muted)',
                          background: 'var(--panel-3)',
                          padding: '1px 6px',
                          borderRadius: 8,
                          border: '1px solid var(--border)',
                          textTransform: 'uppercase',
                          letterSpacing: '.04em',
                        }}
                      >
                        Already assigned
                      </span>
                    ) : matches ? (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--good)',
                          background: 'var(--good-soft)',
                          padding: '1px 6px',
                          borderRadius: 8,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                        }}
                        title={`Assigned to "${targetLabel}"`}
                      >
                        <Icon of={Icons.status.success} size={10} /> match
                      </span>
                    ) : null}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 11,
                      color: 'var(--muted)',
                      marginTop: 2,
                    }}
                  >
                    {u.occupation || '(no orientation)'}
                    {!u.bound ? ' · pending sign-in' : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Compose the "to <names>" line for the post-Assign toast. Resolves
 * each email to its display_name from the live user roster when one is
 * set, falls back to the raw email otherwise. Caps the inline list at
 * a small N and rolls the overflow into a "(+K more)" tail so a 50-
 * person cohort doesn't blow out the toast.
 */
function formatAssigneeList(
  emails: string[],
  users: UserRow[],
  inlineCap: number = 3,
): string {
  if (emails.length === 0) return '(no one)';
  const byEmail = new Map(users.map((u) => [u.email, u]));
  const labels = emails.map((e) => {
    const u = byEmail.get(e);
    const name = u?.displayName?.trim();
    return name && name !== e ? `${name} (${e})` : e;
  });
  if (labels.length <= inlineCap) return labels.join(', ');
  const head = labels.slice(0, inlineCap).join(', ');
  return `${head} (+${labels.length - inlineCap} more)`;
}
